/**
 * UTM Parameter Tracking Utility
 *
 * Captures UTM parameters from URL to track which social media platform
 * or marketing campaign brought users to the site.
 *
 * Usage:
 * - TikTok: yoursite.com/?utm_source=tiktok&utm_medium=social&utm_campaign=bio_link
 * - YouTube: yoursite.com/?utm_source=youtube&utm_medium=social&utm_campaign=video_desc
 * - Instagram: yoursite.com/?utm_source=instagram&utm_medium=social&utm_campaign=bio_link
 */

import { appInsights } from '../services/telemetry';

export interface UTMParams {
  utm_source?: string; // e.g., 'tiktok', 'youtube', 'instagram'
  utm_medium?: string; // e.g., 'social', 'email', 'cpc'
  utm_campaign?: string; // e.g., 'spring_launch', 'bio_link'
  utm_term?: string; // e.g., search terms (optional)
  utm_content?: string; // e.g., differentiate links (optional)
  referrer?: string; // document.referrer for additional context
}

const UTM_STORAGE_KEY = 'pml_utm_params';

// Guard to prevent registering multiple telemetry initializers
let telemetryInitializerRegistered = false;

/**
 * Parse UTM parameters from the current URL
 */
export const parseUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search);

  const utmParams: UTMParams = {};

  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_term = params.get('utm_term');
  const utm_content = params.get('utm_content');

  if (utm_source) utmParams.utm_source = utm_source;
  if (utm_medium) utmParams.utm_medium = utm_medium;
  if (utm_campaign) utmParams.utm_campaign = utm_campaign;
  if (utm_term) utmParams.utm_term = utm_term;
  if (utm_content) utmParams.utm_content = utm_content;

  // Also capture the referrer for additional context
  if (document.referrer) {
    utmParams.referrer = document.referrer;
  }

  return utmParams;
};

/**
 * Store UTM params in session storage to persist across page navigations
 */
export const storeUTMParams = (params: UTMParams): void => {
  if (Object.keys(params).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  }
};

/**
 * Retrieve stored UTM params from session storage
 */
export const getStoredUTMParams = (): UTMParams | null => {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Track UTM parameters in Application Insights
 */
export const trackUTMParams = (params: UTMParams): void => {
  if (Object.keys(params).length === 0) return;

  try {
    // Track as a custom event
    appInsights.trackEvent(
      { name: 'UTM_Referral' },
      {
        ...params,
        landingPage: window.location.pathname,
        timestamp: new Date().toISOString(),
      }
    );

    // Enrich all subsequent events with UTM data via telemetry initializer
    // Only register once to avoid duplicate data on events
    if (params.utm_source && !telemetryInitializerRegistered) {
      telemetryInitializerRegistered = true;
      appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.data) {
          envelope.data.utm_source = params.utm_source;
          envelope.data.utm_medium = params.utm_medium;
          envelope.data.utm_campaign = params.utm_campaign;
        }
        return true;
      });
    }
  } catch (error) {
    console.warn('Failed to track UTM params:', error);
  }
};

/**
 * Initialize UTM tracking - call this once when the app loads
 * Returns the detected UTM params for reference
 */
export const initUTMTracking = (): UTMParams => {
  // Check if we already have stored params (user navigated within site)
  const storedParams = getStoredUTMParams();

  // Parse current URL params
  const currentParams = parseUTMParams();

  // If current URL has UTM params, they take precedence (new visit from social)
  const hasNewUTMParams = currentParams.utm_source || currentParams.utm_medium;

  if (hasNewUTMParams) {
    // Store the new params
    storeUTMParams(currentParams);
    // Track the referral
    trackUTMParams(currentParams);
    return currentParams;
  }

  // Use stored params if available (continuing session)
  if (storedParams) {
    return storedParams;
  }

  // No UTM params - check for referrer-based detection
  const referrerParams = detectReferrerSource();
  if (referrerParams.utm_source) {
    storeUTMParams(referrerParams);
    trackUTMParams(referrerParams);
    return referrerParams;
  }

  return {};
};

/**
 * Detect social media source from document.referrer
 * Fallback when UTM params aren't available
 */
const detectReferrerSource = (): UTMParams => {
  const referrer = document.referrer.toLowerCase();

  if (!referrer) return {};

  const socialPlatforms: Record<string, string> = {
    'tiktok.com': 'tiktok',
    'youtube.com': 'youtube',
    'youtu.be': 'youtube',
    'instagram.com': 'instagram',
    'facebook.com': 'facebook',
    'twitter.com': 'twitter',
    'x.com': 'twitter',
    'linkedin.com': 'linkedin',
    'reddit.com': 'reddit',
  };

  for (const [domain, source] of Object.entries(socialPlatforms)) {
    if (referrer.includes(domain)) {
      return {
        utm_source: source,
        utm_medium: 'social',
        referrer: referrer,
      };
    }
  }

  // Return referrer even if not a known social platform
  return { referrer };
};

/**
 * Get a friendly name for the referral source
 */
export const getReferralSourceName = (params: UTMParams): string | null => {
  if (!params.utm_source) return null;

  const sourceNames: Record<string, string> = {
    tiktok: 'TikTok',
    youtube: 'YouTube',
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'Twitter/X',
    linkedin: 'LinkedIn',
    reddit: 'Reddit',
  };

  return sourceNames[params.utm_source.toLowerCase()] || params.utm_source;
};
